import { createSlice } from '@reduxjs/toolkit'
import {
  createFeedback,
  fetchAllFeedback,
  updateFeedback,
  fetchFeedbackAnalytics,
  updateFeedbackStatus,
  markFeedbackOpened,
  forwardFeedback,
  replyToFeedback,
  markFeedbackUnread,
} from '../actions/feedbackActions.js'

const initialState = {
  feedback: [], // Ensure this is always an array
  currentFeedback: null,
  analytics: null,
  loading: false,
  error: null,
  statusMessage: null,
}

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    resetFeedbackState: state => {
      state.feedback = [] // Reset to empty array, not null
      state.loading = false
      state.error = null
    },
    setStatusMessage: (state, action) => {
      state.statusMessage = action.payload
    },
    getFeedbackRequest: state => {
      state.loading = true
      state.error = null
    },
    getFeedbackSuccess: (state, action) => {
      state.loading = false
      state.currentFeedback = action.payload
    },
    getFeedbackFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    updateFeedbackStatusRequest: state => {
      state.loading = true
      state.error = null
    },
    updateFeedbackStatusSuccess: (state, action) => {
      state.loading = false
      state.currentFeedback = action.payload
    },
    updateFeedbackStatusFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    patchFeedbackRequest: state => {
      state.loading = true
      state.error = null
    },
    patchFeedbackSuccess: (state, action) => {
      state.loading = false
      state.currentFeedback = action.payload
    },
    patchFeedbackFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addCase(createFeedback.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(createFeedback.fulfilled, (state, action) => {
        // Ensure feedback is array before pushing
        if (Array.isArray(state.feedback)) {
          state.feedback.push(action.payload.feedback)
        } else {
          state.feedback = [action.payload.feedback]
        }
        state.loading = false
      })
      .addCase(createFeedback.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(fetchAllFeedback.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllFeedback.fulfilled, (state, action) => {
        // Ensure we always set an array, even if payload is null/undefined
        state.feedback = Array.isArray(action.payload) ? action.payload : []
        state.loading = false
      })
      .addCase(fetchAllFeedback.rejected, (state, action) => {
        state.error = action.payload
        state.feedback = [] // Set to empty array on error
        state.loading = false
      })
      .addCase(updateFeedback.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateFeedback.fulfilled, (state, action) => {
        const updatedFeedback = action.payload
        // Ensure feedback is an array before finding index
        if (Array.isArray(state.feedback)) {
          const feedbackIndex = state.feedback.findIndex(feedback => feedback._id === updatedFeedback._id)
          if (feedbackIndex !== -1) {
            state.feedback[feedbackIndex] = {
              ...state.feedback[feedbackIndex],
              ...updatedFeedback,
            }
          }
        }

        // Update currentFeedback if it's the active one
        if (state.currentFeedback?._id === updatedFeedback._id) {
          state.currentFeedback = {
            ...state.currentFeedback,
            ...updatedFeedback,
          }
        }

        state.loading = false
      })
      .addCase(updateFeedback.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(fetchFeedbackAnalytics.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFeedbackAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload
        state.loading = false
      })
      .addCase(fetchFeedbackAnalytics.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(updateFeedbackStatus.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateFeedbackStatus.fulfilled, (state, action) => {
        const updatedFeedback = action.payload
        // Ensure feedback is an array before finding index
        if (Array.isArray(state.feedback)) {
          const feedbackIndex = state.feedback.findIndex(feedback => feedback._id === updatedFeedback._id)
          if (feedbackIndex !== -1) {
            state.feedback[feedbackIndex] = {
              ...state.feedback[feedbackIndex],
              ...updatedFeedback,
            }
          }
        }

        // Update currentFeedback if it's the active one
        if (state.currentFeedback?._id === updatedFeedback._id) {
          state.currentFeedback = {
            ...state.currentFeedback,
            ...updatedFeedback,
          }
        }

        state.loading = false
      })
      .addCase(updateFeedbackStatus.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(markFeedbackOpened.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(markFeedbackOpened.fulfilled, (state, action) => {
        const updatedFeedback = action.payload
        // Ensure feedback is an array before finding index
        if (Array.isArray(state.feedback)) {
          const feedbackIndex = state.feedback.findIndex(feedback => feedback._id === updatedFeedback._id)
          if (feedbackIndex !== -1) {
            state.feedback[feedbackIndex] = {
              ...state.feedback[feedbackIndex],
              ...updatedFeedback,
            }
          }
        }

        // Update currentFeedback if it's the active one
        if (state.currentFeedback?._id === updatedFeedback._id) {
          state.currentFeedback = {
            ...state.currentFeedback,
            ...updatedFeedback,
          }
        }

        state.loading = false
      })
      .addCase(markFeedbackOpened.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      // Forward feedback
      .addCase(forwardFeedback.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(forwardFeedback.fulfilled, (state, action) => {
        state.loading = false
        state.statusMessage = 'Feedback forwarded successfully'
      })
      .addCase(forwardFeedback.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      // Reply to feedback
      .addCase(replyToFeedback.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(replyToFeedback.fulfilled, (state, action) => {
        const updatedFeedback = action.payload
        // Update feedback in the list if it exists
        if (Array.isArray(state.feedback)) {
          const feedbackIndex = state.feedback.findIndex(feedback => feedback._id === updatedFeedback._id)
          if (feedbackIndex !== -1) {
            state.feedback[feedbackIndex] = {
              ...state.feedback[feedbackIndex],
              ...updatedFeedback,
            }
          }
        }
        // Update currentFeedback if it's the active one
        if (state.currentFeedback?._id === updatedFeedback._id) {
          state.currentFeedback = {
            ...state.currentFeedback,
            ...updatedFeedback,
          }
        }
        state.loading = false
        state.statusMessage = 'Reply sent successfully'
      })
      .addCase(replyToFeedback.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      // Mark feedback as unread
      .addCase(markFeedbackUnread.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(markFeedbackUnread.fulfilled, (state, action) => {
        const updatedFeedback = action.payload
        // Update feedback in the list if it exists
        if (Array.isArray(state.feedback)) {
          const feedbackIndex = state.feedback.findIndex(feedback => feedback._id === updatedFeedback._id)
          if (feedbackIndex !== -1) {
            state.feedback[feedbackIndex] = {
              ...state.feedback[feedbackIndex],
              ...updatedFeedback,
            }
          }
        }
        // Update currentFeedback if it's the active one
        if (state.currentFeedback?._id === updatedFeedback._id) {
          state.currentFeedback = {
            ...state.currentFeedback,
            ...updatedFeedback,
          }
        }
        state.loading = false
        state.statusMessage = 'Feedback marked as unread'
      })
      .addCase(markFeedbackUnread.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
  },
})

export const {
  resetFeedbackState,
  setStatusMessage,
  getFeedbackRequest,
  getFeedbackSuccess,
  getFeedbackFail,
  updateFeedbackStatusRequest,
  updateFeedbackStatusSuccess,
  updateFeedbackStatusFail,
  patchFeedbackRequest,
  patchFeedbackSuccess,
  patchFeedbackFail,
} = feedbackSlice.actions

export default feedbackSlice.reducer